import type { RuleItem, RuleItemWithSubscription } from '~/api/generated-api';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import {
  type AlertCardAssetAlerts,
  AlertCategory,
  AlertContentVariableMapKeys,
  type AlertDetailsMap,
  AlertEvent,
  AlertType,
  type AlertUnread,
  type ApiAlertDetail,
  type ApiAlertEntity,
  type ApiAlertUnread,
  type AssetAlert,
  type AvailableSubscription,
  type DiagnosticEvent,
  type ReceiptPreferenceType,
  type SafetyRegistration,
  type SubscriptionDetails,
  type SubscriptionType,
} from '~/common/models/alert.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import { VehicleSubscriberType } from '~/common/models/notification.model';
import type { EmailFrequency } from '~/common/models/settings.model';
import { toDateStringWithTimezone } from '~/common/utils/date-time.utils';
import { mapApiResponseToType } from '~/features/assets/mappers/asset.mappers';
import type { FleetIdsAndNames } from '~/features/users/utils/userListViewForms.utils';
import type { NOTIFICATION_SETTINGS_MESSAGES } from '~/routes/settings/settings.messages';
import {
  collapseTwelveVoltBatteryEvents,
  getDiagnosticEventName,
} from '~/routes/settings/settings.utils';
import type { AlertParams } from '~/store/alerts/alertsSlice';
import type { AllowedContexts } from '~/store/profile/profileSlice';
import {
  EMER_EVENT_NAME_KEY_BY_EVENT,
  isEmerAlertEvent,
} from '~/utils/alerts/emerAlerts.utils';

export const mapAlertApiEntityToAlertHistoryDetail = (
  {
    sourceId: assetId,
    sourceType,
    lastContentVariableMap,
    ...apiAlertDetail
  }: Partial<ApiAlertEntity>,
  { organizationsId = '' }: AlertParams,
  allowedContexts?: AllowedContexts
): AlertCardAssetAlerts => {
  if (assetId && sourceType === 'asset') {
    const hubId = lastContentVariableMap?.[AlertContentVariableMapKeys.HUB_ID];
    const fleetId =
      lastContentVariableMap?.[AlertContentVariableMapKeys.FLEET_ID];
    const model = lastContentVariableMap?.[
      AlertContentVariableMapKeys.ASSET_TYPE
    ] as string;
    const assetName =
      lastContentVariableMap?.[AlertContentVariableMapKeys.ALERT_OBJECT_ALIAS];
    return {
      assetId,
      assetName,
      type: model ? mapApiResponseToType({ model }) : undefined,
      hub: hubId
        ? {
            id: hubId,
            name: allowedContexts?.[organizationsId]?.hubs?.[hubId]?.name,
          }
        : undefined,
      fleet: fleetId
        ? {
            id: fleetId,
            name: hubId
              ? allowedContexts?.[organizationsId]?.hubs?.[hubId]?.fleets?.[
                  fleetId
                ]?.name
              : undefined,
          }
        : undefined,
    } as AlertCardAssetAlerts;
  }

  throw new BDError('Unexpected alert response', {
    type: BDAppErrorType.VALIDATION,
    data: apiAlertDetail,
  });
};

export const mapAlertApiToAlertDetail = (
  {
    id,
    event,
    read,
    organizationId,
    subscriptionId,
    ...apiAlertDetail
  }: Partial<ApiAlertDetail>,
  allowedContexts?: AllowedContexts
): AssetAlert => {
  if (id && event?.eventTimestamp) {
    const hubId =
      event.contentVariableMap?.[AlertContentVariableMapKeys.HUB_ID];
    const fleetId =
      event.contentVariableMap?.[AlertContentVariableMapKeys.FLEET_ID];
    const model = event.contentVariableMap?.[
      AlertContentVariableMapKeys.ASSET_TYPE
    ] as string;
    return {
      id,
      assetName:
        event.contentVariableMap?.[
          AlertContentVariableMapKeys.ALERT_OBJECT_ALIAS
        ],
      type: model ? mapApiResponseToType({ model }) : undefined,
      hub: hubId
        ? {
            id: hubId,
            name: organizationId
              ? allowedContexts?.[organizationId]?.hubs?.[hubId]?.name
              : undefined,
          }
        : undefined,
      fleet: fleetId
        ? {
            id: fleetId,
            name:
              organizationId && hubId
                ? allowedContexts?.[organizationId]?.hubs?.[hubId]?.fleets?.[
                    fleetId
                  ]?.name
                : undefined,
          }
        : undefined,
      eventType: event.event,
      eventTimestamp: event?.eventTimestamp
        ? toDateStringWithTimezone(event?.eventTimestamp)
        : undefined,
      contentVariableMap: event?.contentVariableMap
        ? event.contentVariableMap
        : undefined,
      read,
      subscriptionId,
    } as AssetAlert;
  }

  throw new BDError('Unexpected alert response', {
    type: BDAppErrorType.VALIDATION,
    data: apiAlertDetail,
  });
};

export const mapAlertUnreadApiResponseToAlertUnread = (
  apiAlertUnread: Partial<ApiAlertUnread>
): AlertUnread => {
  return {
    ...apiAlertUnread,
    hasUnread: apiAlertUnread.hasUnread,
    approximateCount: apiAlertUnread.approximateCount,
  };
};

export const mapAlertMessage = (
  alertType: string,
  labels: {
    [index: string]: string;
  }
): string => {
  switch (alertType) {
    case AlertType.LOW_BATTERY:
    case AlertType.LOW_BRAKE_FLUID:
    case AlertType.LOW_WASHER_FLUID:
      return labels['alerts:feed.detail.isLowOn'];
    case AlertType.LOW_TIRE_PRESSURE:
      return labels['alerts:feed.detail.zevoHasLowTirePressure'];
    case AlertType.FLAT_TIRE:
      return labels['alerts:feed.detail.zevoHasFlatTire'];
    default:
      return DEFAULT_EMPTY_VALUE;
  }
};

export const mapAlertStatus = (
  alertType: string,
  variableMap: AlertDetailsMap,
  labels: {
    [index: string]: string;
  }
): string => {
  switch (alertType) {
    case AlertType.LOW_BATTERY:
      return variableMap?.battery_soc || variableMap?.battery_soc === 0
        ? `${labels['alerts:feed.detail.charge']} (${variableMap.battery_soc}%)`
        : '';
    case AlertType.LOW_BRAKE_FLUID:
      return labels['alerts:feed.detail.brakeFluid'];
    case AlertType.LOW_WASHER_FLUID:
      return labels['alerts:feed.detail.washerFluid'];
    default:
      return DEFAULT_EMPTY_VALUE;
  }
};

export const mapAlertEventName = (apiEvent: string): string => {
  switch (apiEvent) {
    case AlertEvent.ACCOUNT_UPDATE:
      return translateText('settings:notification.card.name.profileUpdates');
    case AlertEvent.REMOVE_USER:
      return translateText('settings:notification.card.name.removeUser');
    case AlertEvent.ADD_USER:
      return translateText('settings:notification.card.name.addUser');
    case AlertEvent.BATCH_ONBOARDING:
    // Temporarily mapping the deprecated vehicle onboarding event to the batch onboarding name to prevent blank notification setting names.
    // TODO: Remove the deprecated vehicle onboarding event once we confirm the events are no longer used in the backend service and deleted from the databases.
    case AlertEvent.DEPRECATED_ONBOARDING_VEHICLE:
      return translateText('settings:notification.card.name.batchOnboarding');
    case AlertEvent.BATCH_OFFBOARDING:
    // Temporarily mapping the deprecated vehicle offboarding event to the batch offboarding name to prevent blank notification setting names.
    // TODO: Remove the deprecated vehicle offboarding event once we confirm the events are no longer used in the backend service and deleted from the databases.
    case AlertEvent.DEPRECATED_OFFBOARDING_VEHICLE:
      return translateText('settings:notification.card.name.batchOffboarding');
    case AlertEvent.DEFAULT_DIAGNOSTICS:
      return translateText(
        'settings:notification.card.name.defaultDiagnostics'
      );
    case AlertEvent.LOCATION_MASK_UNMASK:
      return translateText(
        'settings:notification.card.name.locationMaskUnmask'
      );
    case AlertEvent.GEOFENCE_ENTRY_EXIT:
      return translateText('settings:notification.card.name.geofenceAlerts');
    case AlertEvent.VEHICLE_DRIVE_BLOCK:
      return translateText('settings:notification.card.name.driveBlockAlerts');
    case AlertEvent.VEHICLE_OFF_HOURS:
      return translateText(
        'settings:notifications.vehicles.vehicleOffHoursNotifications.name'
      );
    case AlertEvent.VEHICLE_INACTIVITY:
      return translateText(
        'settings:notifications.vehicles.vehicleInactivityNotifications.name'
      );
    case AlertEvent.VEHICLE_IDLING:
      return translateText(
        'settings:notifications.vehicles.vehicleIdlingNotifications.name'
      );
    case AlertEvent.LOW_TRIP_SCORE:
      return translateText('alerts:cameraAlerts.low-trip-score');
    case AlertEvent.SAFETY_EVENT:
      return translateText('alerts:cameraAlerts.safety-event');
    case AlertEvent.VIDEO_CLIP_REQUEST_STATUS:
      return translateText('alerts:cameraAlerts.video-clip-request-status');
    default:
      return isEmerAlertEvent(apiEvent)
        ? translateText(EMER_EVENT_NAME_KEY_BY_EVENT[apiEvent])
        : DEFAULT_EMPTY_VALUE;
  }
};

/**
 * Maps alert event to its display name with optional pluralization support
 * @param apiEvent - The alert event type
 * @param count - Optional count for pluralization (uses i18next pluralization rules)
 * @returns The translated event name
 */
export const mapAlertEventNameWithCount = (
  apiEvent: string,
  count?: number
): string => {
  switch (apiEvent) {
    case AlertEvent.BATCH_ONBOARDING:
    case AlertEvent.DEPRECATED_ONBOARDING_VEHICLE:
      return translateText('settings:notification.card.name.batchOnboarding', {
        count,
      });
    case AlertEvent.DEPRECATED_OFFBOARDING_VEHICLE:
    case AlertEvent.BATCH_OFFBOARDING:
      return translateText('settings:notification.card.name.batchOffboarding', {
        count,
      });
    case AlertEvent.CAMERA_ADDED:
      return translateText('settings:notification.card.name.cameraAdded', {
        count,
      });
    default:
      return isEmerAlertEvent(apiEvent)
        ? translateText(EMER_EVENT_NAME_KEY_BY_EVENT[apiEvent], { count })
        : mapAlertEventName(apiEvent);
  }
};

export const mapSubscriptionTypesToAvailableSubscriptions = (
  type: SubscriptionType
): AvailableSubscription => {
  return {
    id: '',
    name: mapAlertEventName(type.event),
    category: type.category as AlertCategory,
    event: type.event as AlertEvent,
    selectId: `${type.category}*${type.event}`,
    receiptPreferences: [],
    eventOptions: [],
  };
};

export const mapSubscriptionsToAvailableSubscription = (
  sub: SubscriptionDetails,
  labels: typeof NOTIFICATION_SETTINGS_MESSAGES,
  isVehicleHealthEnabled = false
): AvailableSubscription => {
  const {
    id,
    organizationId,
    profileId,
    active,
    registrations,
    receiptPreferences,
    preferredName,
    assets,
    grouped,
    toggleActive,
    emailFrequency,
  } = sub;
  const category = registrations[0].category;
  const diagnosticRegistrations =
    category === AlertCategory.DIAGNOSTIC
      ? collapseTwelveVoltBatteryEvents(registrations, isVehicleHealthEnabled)
      : registrations;
  // A subscription holding only the stripped twelve-volt-battery has no event left to name.
  const event = (diagnosticRegistrations[0]?.event ??
    AlertEvent.DEFAULT_DIAGNOSTICS) as AlertEvent;
  const name =
    category === AlertCategory.DIAGNOSTIC
      ? preferredName
        ? preferredName
        : labels['settings:notification.card.name.defaultDiagnostics']
      : mapAlertEventName(event);

  const nonLocationMaskPreferredName =
    event !== AlertEvent.LOCATION_MASK_UNMASK ? preferredName : undefined;

  return {
    id: id || '',
    name,
    subscriptionId: id,
    organizationId,
    profileId,
    active,
    toggleActive,
    category,
    event,
    grouped,
    emailFrequency,
    selectId: `${category}*${event}`,
    preferredName: nonLocationMaskPreferredName,
    receiptPreferences: receiptPreferences || [],
    eventOptions:
      category === AlertCategory.DIAGNOSTIC
        ? diagnosticRegistrations.map((item) => {
            return {
              event: item.event,
              category: item.category,
              selectId: `${item.category}*${item.event}`,
              active: true,
              id: id || '',
              displayName: getDiagnosticEventName(
                item.event as DiagnosticEvent,
                labels,
                isVehicleHealthEnabled
              ),
            };
          })
        : [],
    ...(assets?.length && {
      selectedAssets: assets,
    }),
    selectedVehicleSubscriberType: assets?.length
      ? VehicleSubscriberType.SELECTED_VEHICLES
      : VehicleSubscriberType.ALL_VEHICLES,
  };
};

export const cameraNotificationsOrder = [
  AlertEvent.SAFETY_EVENT,
  AlertEvent.LOW_TRIP_SCORE,
  AlertEvent.ADMIN_PIN_CHANGED,
  AlertEvent.CAMERA_NOT_RECORDING,
  AlertEvent.LENS_OBSTRUCTED,
  AlertEvent.CAMERA_ADDED,
  AlertEvent.VIDEO_CLIP_REQUEST_STATUS,
  AlertEvent.CAMERA_DELETED,
  AlertEvent.CAMERA_OFFLINE,
];
export const mapSafetySubscriptionToAvailableSubscription = (
  subscription: SubscriptionDetails
): AvailableSubscription => {
  const {
    id,
    organizationId,
    registrations,
    receiptPreferences,
    active,
    toggleActive,
    grouped,
    emailFrequency,
    assets,
  } = subscription;

  const item = registrations[0] ?? { event: '', category: '' };
  const event = item.event as AlertEvent;
  const isOffHoursOrIdling =
    event === AlertEvent.VEHICLE_OFF_HOURS ||
    event === AlertEvent.VEHICLE_IDLING;

  return {
    id: id || '',
    organizationId,
    name: translateText(`alerts:cameraAlerts.${item.event}`),
    preferredName: translateText(`alerts:cameraAlerts.${item.event}`),
    receiptPreferences: receiptPreferences || [],
    event,
    category: item.category,
    selectId: '',
    eventOptions: [],
    subTypes: item.subTypes || [],
    active,
    toggleActive,
    grouped,
    emailFrequency,
    ...(isOffHoursOrIdling && assets?.length ? { selectedAssets: assets } : {}),
    ...(isOffHoursOrIdling
      ? {
          selectedVehicleSubscriberType: assets?.length
            ? VehicleSubscriberType.SELECTED_VEHICLES
            : VehicleSubscriberType.ALL_VEHICLES,
        }
      : {}),
  };
};

export const mapGeofenceNotificationListApiItemToAvailableSubscription = (
  item: RuleItemWithSubscription,
  allFleets: FleetIdsAndNames[]
): AvailableSubscription => {
  const {
    ruleId,
    name,
    assignedFleetIds = [],
    category,
    event: ruleEvent,
    subscription,
    geofenceCount = 0,
  } = item;
  const fleetNames = allFleets
    .filter((fleet) => assignedFleetIds.includes(fleet.fleetId))
    .map((fleet) => fleet.name);

  const event = (
    subscription?.registrations &&
    subscription.registrations.length > 0 &&
    subscription.registrations[0].event
      ? subscription.registrations[0].event
      : ruleEvent
  ) as AlertEvent;
  return {
    id: subscription?.id ?? '',
    subscriptionId: subscription?.id,
    name,
    active: subscription?.active ?? false,
    toggleActive: subscription?.toggleActive ?? false,
    event,
    category: AlertCategory.GEOFENCE,
    selectId: `${category}*${event}*${ruleId}`,
    eventOptions: [],
    receiptPreferences:
      (subscription?.receiptPreferences as ReceiptPreferenceType[]) ?? [],
    grouped: subscription?.grouped,
    emailFrequency: subscription?.emailFrequency as EmailFrequency,
    assignedFleets: fleetNames,
    geofenceCount,
    ruleId,
  };
};
export const mapRuleItemToAvailableSubscription = (
  item: RuleItem,
  allFleets: FleetIdsAndNames[],
  geofenceCount?: number
): AvailableSubscription => {
  const {
    id,
    name,
    assignedFleetIds = [],
    type,
    event: ruleEvent,
    subscription,
  } = item;

  const fleetNames = allFleets
    .filter((fleet) => assignedFleetIds.includes(fleet.fleetId))
    .map((fleet) => fleet.name);

  const event = (
    subscription?.registrations &&
    subscription.registrations.length > 0 &&
    subscription.registrations[0].event
      ? subscription.registrations[0].event
      : ruleEvent
  ) as AlertEvent;

  // Map type to category
  const categoryMap = {
    asset: AlertCategory.ASSET,
    geofence: AlertCategory.GEOFENCE,
    'drive-block': AlertCategory.DRIVE_BLOCK,
    'safety-and-security': AlertCategory.SAFETY,
  };

  return {
    id: subscription?.id ?? '',
    subscriptionId: subscription?.id,
    name,
    active: subscription?.active ?? false,
    toggleActive: subscription?.toggleActive ?? false,
    event,
    category: categoryMap[type],
    selectId: `${type}*${event}*${id}`,
    eventOptions: [],
    receiptPreferences:
      (subscription?.receiptPreferences as ReceiptPreferenceType[]) ?? [],
    grouped: subscription?.grouped,
    emailFrequency: subscription?.emailFrequency as EmailFrequency,
    assignedFleets: fleetNames,
    geofenceCount,
    ruleId: id,
  };
};

export const mapRegistrationToAvailableSubscription = (
  registration: SafetyRegistration
): AvailableSubscription => {
  const { id, event, category, subTypes } = registration;
  const isOffHoursOrIdling =
    event === AlertEvent.VEHICLE_OFF_HOURS ||
    event === AlertEvent.VEHICLE_IDLING;
  return {
    id: id || '',
    name: translateText(`alerts:cameraAlerts.${event}`),
    event,
    category,
    selectId: `${category}*${event}`,
    eventOptions: [],
    receiptPreferences: [],
    subTypes: subTypes || [],
    ...(isOffHoursOrIdling
      ? {
          selectedVehicleSubscriberType: VehicleSubscriberType.ALL_VEHICLES,
        }
      : {}),
  };
};
